"use client";

import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import { Button } from "@/app/(public)/shared/ui/Button";
import { StringInput } from "@/components/ui/shared/SpecialInput";
import { newsletterSignup } from "@/lib/database/client/database";
import { getCurrentUser } from "@/lib/db/users";
import { createClient } from "@/utils/supabase/client";
import { motion } from "motion/react";
import { useEffect, useState } from "react";

export const NewsletterForm = () => {
  const supabase = createClient();

  const [userValid, setUserValid] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [comments, setComments] = useState("");

  const [showConfirmation, setShowConfirmation] = useState(false);

  const [firstNameTouched, setFirstNameTouched] = useState(false);
  const [lastNameTouched, setLastNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [submitMessage, setSubmitMessage] = useState("");

  const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleFirstNameChange = (value: string) => {
    setFirstNameTouched(true);
    setFirstName(value);
  };

  const handleLastNameChange = (value: string) => {
    setLastNameTouched(true);
    setLastName(value);
  };

  const handleEmailChange = (value: string) => {
    setEmailTouched(true);
    setEmail(value);
  };

  const submitForm = async () => {
    setFirstNameTouched(true);
    setLastNameTouched(true);
    setEmailTouched(true);
    setSubmitStatus("idle");
    setSubmitMessage("");

    if (!firstName || !lastName || !email || !isValidEmail(email)) {
      setSubmitStatus("error");
      setSubmitMessage("Please fill in all required fields with valid information.");
      return;
    }

    setSubmitting(true);
    try {
      await newsletterSignup(supabase, firstName, lastName, email, comments);
      setFirstName("");
      setLastName("");
      setEmail("");
      setComments("");
      setShowConfirmation(true);
      setFirstNameTouched(false);
      setLastNameTouched(false);
      setEmailTouched(false);
    } catch (err) {
      setSubmitStatus("error");
      setSubmitMessage("Submission failed. Please try again later.");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (
      submitStatus === "error" &&
      submitMessage === "Please fill in all required fields with valid information." &&
      firstName &&
      lastName &&
      email &&
      isValidEmail(email)
    ) {
      setSubmitStatus("idle");
      setSubmitMessage("");
    }
  }, [firstName, lastName, email, submitStatus, submitMessage]);

  useEffect(() => {
    (async () => {
      const user = await getCurrentUser(supabase);

      if (user) {
        setUserValid(true);
        setFirstName(user.first_name);
        setLastName(user.last_name);
        setEmail(user.email);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-landing-primary overflow-x-hidden container-page">
      {/* Hero Section with Left-Right Layout */}
      <section className="container-top">
        <div className="my-12 lg:my-24 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 h-full">
          {/* Left Content */}
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-6">
              <h2 className="text-h2 text-landing-primary">Stay Updated with Nowadays</h2>
              <p className="text-body-lg text-landing-secondary max-w-530">
                Subscribe to our newsletter to get the latest updates on our product, company news, and event planning
                insights.
              </p>
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                  <span className="text-body text-landing-secondary-sand">Product updates and new features</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                  <span className="text-body text-landing-secondary-sand">Event planning tips and insights</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                  <span className="text-body text-landing-secondary-sand">Company news and announcements</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="flex items-start justify-center">
            <motion.div
              className="w-full max-w-md"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-h4 text-landing-primary-sand">Subscribe to Newsletter</h3>

                  <div>
                    <label className="text-body-sm font-medium text-landing-primary-sand mb-2 block">First Name*</label>
                    <StringInput
                      className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                      placeholder="Ex: John"
                      value={firstName}
                      setValue={handleFirstNameChange}
                      validator={firstNameTouched ? firstName !== "" : true}
                    />
                    {firstNameTouched && !firstName && (
                      <div className="text-red-500 text-sm mt-1" role="alert">
                        First name is required.
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-body-sm font-medium text-landing-primary-sand mb-2 block">Last Name*</label>
                    <StringInput
                      className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                      placeholder="Ex: Doe"
                      value={lastName}
                      setValue={handleLastNameChange}
                      validator={lastNameTouched ? lastName !== "" : true}
                    />
                    {lastNameTouched && !lastName && (
                      <div className="text-red-500 text-sm mt-1" role="alert">
                        Last name is required.
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-body-sm font-medium text-landing-primary-sand mb-2 block">Email*</label>
                    <StringInput
                      className="w-full input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors"
                      placeholder="Ex: john@doe.com"
                      value={email}
                      setValue={handleEmailChange}
                      validator={emailTouched ? email !== "" && isValidEmail(email) : true}
                    />
                    {emailTouched && (!email || !isValidEmail(email)) && (
                      <div className="text-red-500 text-sm mt-1" role="alert">
                        {email && !isValidEmail(email) ? "Please enter a valid email." : "Email is required."}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-body-sm font-medium text-landing-primary-sand mb-2 block">
                      Comments, Questions, or Jokes
                    </label>
                    <textarea
                      className="w-full h-24 input input-bordered bg-white border-landing-primary focus:border-landing-cta-light transition-colors resize-none"
                      placeholder="Tell us what you're interested in..."
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  {submitStatus !== "idle" && (
                    <div
                      className={`p-3 rounded-md text-sm ${
                        submitStatus === "success"
                          ? "bg-green-50 text-green-700 border border-green-200 mb-4"
                          : "bg-red-50 text-red-700 border border-red-200 mb-4"
                      }`}
                    >
                      {submitMessage}
                    </div>
                  )}
                  <Button variant="primary" size="md" onClick={submitForm} className="w-full" disabled={submitting}>
                    {submitting ? "Subscribing..." : "Subscribe"}
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div
          className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-black bg-opacity-50 z-50"
          onClick={() => setShowConfirmation(false)}
        >
          <motion.div
            className="bg-white p-8 rounded-2xl max-w-md mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center space-y-4">
              <div className="text-4xl">🎉</div>
              <h2 className="text-h3 text-landing-primary-sand font-bold">Subscribed!</h2>
              <p className="text-body text-landing-secondary-sand">
                We're excited to have you on our journey! You'll receive our latest updates and insights.
              </p>
              <Button variant="primary" size="md" onClick={() => setShowConfirmation(false)}>
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};
